<template>
  <div class="signup-modal__wrapper" data-testid="signup-modal-wrapper">
    <div class="signup-modal__form">
      <div class="signup-modal__branding">
        <ImageSvg name="general.logoWithText" />
      </div>
      <h2><TitleSeparator>Sign up with</TitleSeparator></h2>
      <SocialAuth :redirect-url="props.redirectUrl" @action="onSocialAction" :in-progress="socialInProgress" />
      <p><TitleSeparator>Or</TitleSeparator></p>
      <Form @submit="onSubmit" :validation-schema="schema">
        <fieldset class="flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <label htmlFor="emailInput" class="text-sm font-bold">Work email</label>
            <Field
              type="text"
              name="email"
              id="emailInput"
              data-testid="sign-up-email"
              class="w-full px-3 overflow-y-hidden bg-white bg-no-repeat border outline-none rounded-10 dark:bg-zinc-800 h-9 border-slate-300 dark:border-zinc-500 focus:border-blue-500 focus:ring focus:ring-blue-500/50 active:border-slate-600"
            />
            <ErrorMessage as="p" class="mt-2 text-sm text-slate-500 dark:text-zinc-400" name="email" />
          </div>
          <div class="flex flex-col gap-1">
            <label htmlFor="passwordInput" class="text-sm font-bold">Password</label>
            <Field
              type="password"
              name="password"
              id="passwordInput"
              data-testid="sign-up-password"
              class="w-full px-3 overflow-y-hidden bg-white bg-no-repeat border outline-none rounded-10 dark:bg-zinc-800 h-9 border-slate-300 dark:border-zinc-500 focus:border-blue-500 focus:ring focus:ring-blue-500/50 active:border-slate-600"
            />
            <ErrorMessage as="p" class="mt-2 text-sm text-slate-500 dark:text-zinc-400" name="password" />
          </div>
          <Button class="!w-full" data-testid="sign-up" :disabled="requestInProgress" :loading="requestInProgress" type="solid" variant="blue" :submit="true">
            Sign up
          </Button>
        </fieldset>
      </Form>

      <p class="mt-4 text-sm text-center dark:text-zinc-300">
        Have an account?
        <a
          @click.stop.prevent="showSignInModal(props.redirectUrl)"
          data-testid="sign-in-show-modal"
          href="#"
          class="text-blue-500 underline rounded-sm hover:text-blue-400 focus-visible:ring focus-visible:ring-blue-500/50 focus-visible:bg-blue-500/10 focus:outline-none"
          >Sign in</a
        >
      </p>
    </div>

    <div class="signup-modal__promo" data-testid="signup-promo-box">
      <h2 class="signup-modal__promo__title">Record a Video, Let Zight AI Handle the Rest</h2>
      <ul role="list" class="signup-modal__promo__list">
        <li>✨ Automate documentation</li>
        <li>✨ Skip a meeting</li>
        <li>✨ Save time</li>
      </ul>
      <p>Effortless and secure</p>
      <p>
        <small>Learn more at <a href="https://zight.com/ai/">zight.com/ai</a></small>
      </p>
      <button
        v-if="props.showModalForFreeTier"
        data-testid="signup-skip-button"
        @click="onSkip"
        class="absolute bottom-10 bg-transparent text-white border border-white py-2 px-4 rounded-lg hover:bg-white hover:text-blue-500 focus:ring focus:ring-white/50"
      >
        Skip sign up for now &rarr;
      </button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { object, string } from 'yup';
import { Form, Field, ErrorMessage } from 'vee-validate';
import { useUserStore } from '@/store/useUserStore';
import SocialAuth from './auth_dialog/Social.vue';
import TitleSeparator from './auth_dialog/TitleSeparator.vue';
import { useToast } from '@/composables/useToast';
import { useAuthModals } from '@/composables/useAuthModals';
import { ref } from 'vue';
import { Button, ImageSvg } from '@/storybook';
import { useAuth } from '@/composables/useAuth';

const hasUpperCase = /[A-Z]/;
const hasNumber = /[0-9]/;
const hasLowerCase = /[a-z]/;

const requestInProgress = ref(false);
const userStore = useUserStore();
const { showSignInModal } = useAuthModals();

const props = withDefaults(
  defineProps<{
    redirectUrl?: string;
    sidebarText?: string;
    showModalForFreeTier?: boolean;
    signedUpFrom?: string;
  }>(),
  {
    sidebarText: 'Create videos, GIFs, annotated screenshots and share instantly',
    showModalForFreeTier: false,
    signedUpFrom: undefined,
  }
);

const schema = object().shape({
  email: string().email().required(),
  password: string()
    .required()
    .test('isValidPass', 'Must be at least 8 characters long, contain uppercase letters and a number', (value: string | undefined) => {
      return [hasUpperCase, hasLowerCase, hasNumber].every((val: RegExp) => val.test(value || ''));
    }),
});

const { onSocialAction, inProgress: socialInProgress } = useAuth(props.redirectUrl);

const { error } = useToast();
const emits = defineEmits(['close']);

const onSubmit = async (data: any) => {
  try {
    requestInProgress.value = true;
    const response = await userStore.signUp(data, props.signedUpFrom);

    if (response && props.redirectUrl) {
      window.location.assign(props.redirectUrl);
      return;
    }

    if (response && response.metadata && response.metadata.redirectTo) {
      window.location.assign(response.metadata.redirectTo);
      return;
    }

    await userStore.load();

    requestInProgress.value = false;
    emits('close');
  } catch (err: any) {
    requestInProgress.value = false;
    error(err.message);
  }
};

const onSkip = () => emits('close');
</script>

<style>
.signup-modal__wrapper {
  --auth-form-padding: 2.5rem;

  display: inline-flex;
  flex-wrap: wrap;
  flex-shrink: 1;
  gap: 0.625rem;
  justify-content: center;
}

.signup-modal__form {
  padding-inline: var(--auth-form-padding);
  max-width: calc(18rem + (var(--auth-form-padding) * 2));
}

.signup-modal__branding {
  justify-self: center;
  margin-block-start: 2rem;
}

.signup-modal__branding * {
  display: inline-flex;
  max-height: 1.875rem;
}

.signup-modal__promo {
  display: inline-flex;
  flex-direction: column;
  gap: 1.88rem;
  max-width: 18rem;
  justify-content: center;
  align-items: start;
  padding-inline: 2.34rem;
  padding-block: 3.78rem;

  color: var(--white-copernicus);
  background: var(--gradient-dark-purple);

  border-radius: var(--radius-xl);
}

.signup-modal__promo > * {
  --focus-outline-color: rgba(255, 255, 255, 0.5);

  margin-block-end: 0;
}

.signup-modal__promo__title {
  font-size: var(--promo-title-size, 1.5rem);
  line-height: var(--line-height-headings, 1.1);
  text-wrap: balance;
  text-wrap: pretty;
}
</style>
